import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Input, Select, Button, List, Tag, Space, Modal } from 'antd';
import { SearchOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

interface Template {
  id: string;
  title: string;
  category: string;
  industry: string;
  description: string;
  downloads: number;
}

const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    industry: '全部',
    category: '全部',
    keyword: '',
  });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const industries = ['全部', '通用', '制造业', 'IT/互联网', '金融', '不动产', '医疗'];
  const categories = ['全部', '劳动合同', '租赁合同', '买卖合同', '技术合同', '知识产权', '保密协议'];

  useEffect(() => {
    fetchTemplates();
  }, [filters.industry, filters.category]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/templates', {
        params: {
          industry: filters.industry !== '全部' ? filters.industry : undefined,
          category: filters.category !== '全部' ? filters.category : undefined,
        }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('获取模板失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchTemplates();
  };

  const handleDownload = async (template: Template) => {
    try {
      const response = await api.get(`/download-template/${template.id}`, {
        responseType: 'blob'
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${template.title}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  const handlePreview = (template: Template) => {
    setSelectedTemplate(template);
    setPreviewVisible(true);
  };

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>合同模板库</h1>
      
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="选择行业"
              value={filters.industry}
              onChange={(value) => setFilters({ ...filters, industry: value })}
            >
              {industries.map(industry => (
                <Option key={industry} value={industry}>{industry}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="选择分类"
              value={filters.category}
              onChange={(value) => setFilters({ ...filters, category: value })}
            >
              {categories.map(category => (
                <Option key={category} value={category}>{category}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Input
              placeholder="搜索模板"
              value={filters.keyword}
              onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
              suffix={
                <Button type="text" icon={<SearchOutlined />} onClick={handleSearch} />
              }
            />
          </Col>
          <Col span={4}>
            <Button type="primary" block onClick={handleSearch}>
              搜索
            </Button>
          </Col>
        </Row>
      </Card>

      <List
        loading={loading}
        grid={{ gutter: 16, column: 2 }}
        dataSource={templates}
        renderItem={(item) => (
          <List.Item>
            <Card
              title={item.title}
              extra={
                <Tag color="blue">
                  下载 {item.downloads} 次
                </Tag>
              }
              actions={[
                <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(item)}>
                  预览
                </Button>,
                <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownload(item)}>
                  下载
                </Button>
              ]}
            >
              <p>
                <Tag color="orange">{item.industry}</Tag>
                <Tag color="green">{item.category}</Tag>
              </p>
              <p>{item.description}</p>
            </Card>
          </List.Item>
        )}
      />

      <Modal
        title={selectedTemplate?.title}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>,
          <Button key="download" type="primary" onClick={() => selectedTemplate && handleDownload(selectedTemplate)}>
            下载模板
          </Button>
        ]}
        width={800}
      >
        <div style={{ padding: 20, background: '#f5f5f5', minHeight: 400 }}>
          <p>合同预览功能需要集成文档预览组件</p>
          <p>此处为演示界面，实际项目可集成：</p>
          <ul>
            <li>微软Office Online预览</li>
            <li>Google Docs预览</li>
            <li>PDF.js预览</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
};

export default TemplatesPage;